import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AudioTrimmer({ audioBlob, onSave, onCancel }) {
    const [audioUrl, setAudioUrl] = useState(null);
    const [duration, setDuration] = useState(0);
    const [startTime, setStartTime] = useState(0);
    const [endTime, setEndTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [waveform, setWaveform] = useState([]);
    const [saving, setSaving] = useState(false);
    
    const audioRef = useRef(null);
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const draggingRef = useRef(null);

    useEffect(() => {
        if (audioBlob) {
            const url = URL.createObjectURL(audioBlob);
            setAudioUrl(url);
            
            // Generate waveform
            const reader = new FileReader();
            reader.onload = async () => {
                const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                const buffer = await audioContext.decodeAudioData(reader.result);
                const data = buffer.getChannelData(0);
                const samples = 100;
                const blockSize = Math.floor(data.length / samples);
                const wave = [];
                for (let i = 0; i < samples; i++) {
                    let sum = 0;
                    for (let j = 0; j < blockSize; j++) {
                        sum += Math.abs(data[i * blockSize + j]);
                    }
                    wave.push(sum / blockSize);
                }
                const max = Math.max(...wave);
                setWaveform(wave.map(v => v / max));
                setDuration(buffer.duration);
                setEndTime(buffer.duration);
            };
            reader.readAsArrayBuffer(audioBlob);
            
            return () => URL.revokeObjectURL(url);
        }
    }, [audioBlob]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        
        const handleTime = () => {
            setCurrentTime(audio.currentTime);
            if (audio.currentTime >= endTime) {
                audio.pause();
                setIsPlaying(false);
            }
        };
        const handleEnded = () => setIsPlaying(false);
        
        audio.addEventListener('timeupdate', handleTime);
        audio.addEventListener('ended', handleEnded);
        return () => {
            audio.removeEventListener('timeupdate', handleTime);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [endTime]);

    const playPreview = () => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = startTime;
        audio.play();
        setIsPlaying(true);
    };

    const stopPreview = () => {
        audioRef.current?.pause();
        setIsPlaying(false);
    };

    const handleMouseDown = (e, handle) => {
        e.preventDefault();
        draggingRef.current = handle;
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = useCallback((e) => {
        if (!draggingRef.current || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
        const time = (x / rect.width) * duration;
        
        if (draggingRef.current === 'start') {
            setStartTime(Math.min(time, endTime - 0.1));
        } else {
            setEndTime(Math.max(time, startTime + 0.1));
        }
    }, [duration, startTime, endTime]);

    const handleMouseUp = useCallback(() => {
        draggingRef.current = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove]);

    const handleSave = async () => {
        setSaving(true);
        try {
            // Create trimmed audio
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const reader = new FileReader();
            
            reader.onload = async () => {
                const buffer = await audioContext.decodeAudioData(reader.result);
                const startSample = Math.floor(startTime * buffer.sampleRate);
                const endSample = Math.floor(endTime * buffer.sampleRate);
                const length = endSample - startSample;
                
                const trimmedBuffer = audioContext.createBuffer(
                    buffer.numberOfChannels,
                    length,
                    buffer.sampleRate
                );
                
                for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
                    const oldData = buffer.getChannelData(ch);
                    const newData = trimmedBuffer.getChannelData(ch);
                    for (let i = 0; i < length; i++) {
                        newData[i] = oldData[startSample + i];
                    }
                }
                
                // Convert to blob
                const offlineCtx = new OfflineAudioContext(
                    trimmedBuffer.numberOfChannels,
                    trimmedBuffer.length,
                    trimmedBuffer.sampleRate
                );
                const source = offlineCtx.createBufferSource();
                source.buffer = trimmedBuffer;
                source.connect(offlineCtx.destination);
                source.start();
                
                const rendered = await offlineCtx.startRendering();
                const wav = audioBufferToWav(rendered);
                const newBlob = new Blob([wav], { type: 'audio/wav' });
                
                onSave(newBlob, endTime - startTime);
            };
            
            reader.readAsArrayBuffer(audioBlob);
        } catch (error) {
            console.error('Trim error:', error);
            toast.error('Failed to trim audio');
            setSaving(false);
        }
    };

    const formatTime = (t) => {
        const m = Math.floor(t / 60);
        const s = Math.floor(t % 60);
        const ms = Math.floor((t % 1) * 10);
        return `${m}:${s.toString().padStart(2, '0')}.${ms}`;
    };

    return (
        <div className="space-y-4">
            <audio ref={audioRef} src={audioUrl} />
            
            {/* Waveform */}
            <div 
                ref={containerRef}
                className="relative h-24 bg-muted/30 rounded overflow-hidden"
            >
                {/* Waveform bars */}
                <div className="absolute inset-0 flex items-center justify-around px-1">
                    {waveform.map((v, i) => (
                        <div
                            key={i}
                            className="w-0.5 bg-muted-foreground/40 rounded-full"
                            style={{ height: `${v * 80}%` }}
                        />
                    ))}
                </div>
                
                {/* Trim region */}
                <div
                    className="absolute top-0 bottom-0 bg-primary/10 border-x-2 border-primary"
                    style={{
                        left: `${(startTime / duration) * 100}%`,
                        right: `${100 - (endTime / duration) * 100}%`
                    }}
                />
                
                {/* Start handle */}
                <div
                    className="absolute top-0 bottom-0 w-3 bg-primary cursor-ew-resize flex items-center justify-center"
                    style={{ left: `${(startTime / duration) * 100}%`, transform: 'translateX(-50%)' }}
                    onMouseDown={(e) => handleMouseDown(e, 'start')}
                >
                    <div className="w-0.5 h-8 bg-primary-foreground rounded" />
                </div>
                
                {/* End handle */}
                <div
                    className="absolute top-0 bottom-0 w-3 bg-primary cursor-ew-resize flex items-center justify-center"
                    style={{ left: `${(endTime / duration) * 100}%`, transform: 'translateX(-50%)' }}
                    onMouseDown={(e) => handleMouseDown(e, 'end')}
                >
                    <div className="w-0.5 h-8 bg-primary-foreground rounded" />
                </div>
                
                {/* Playhead */}
                {isPlaying && (
                    <div
                        className="absolute top-0 bottom-0 w-px bg-foreground"
                        style={{ left: `${(currentTime / duration) * 100}%` }}
                    />
                )}
            </div>
            
            {/* Times */}
            <div className="flex justify-between text-xs text-muted-foreground font-mono">
                <span>{formatTime(startTime)}</span>
                <span>Duration: {formatTime(endTime - startTime)}</span>
                <span>{formatTime(endTime)}</span>
            </div>
            
            {/* Controls */}
            <div className="flex gap-2">
                <Button
                    variant="outline"
                    onClick={isPlaying ? stopPreview : playPreview}
                    className="flex-1 h-9"
                >
                    {isPlaying ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Preview
                </Button>
                <Button variant="ghost" onClick={onCancel} className="h-9">
                    <X className="w-4 h-4" />
                </Button>
                <Button onClick={handleSave} disabled={saving} className="h-9">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    Save
                </Button>
            </div>
        </div>
    );
}

// Helper to convert AudioBuffer to WAV
function audioBufferToWav(buffer) {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1;
    const bitDepth = 16;
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    const dataLength = buffer.length * blockAlign;
    const bufferLength = 44 + dataLength;
    const arrayBuffer = new ArrayBuffer(bufferLength);
    const view = new DataView(arrayBuffer);
    
    const writeString = (offset, str) => {
        for (let i = 0; i < str.length; i++) {
            view.setUint8(offset + i, str.charCodeAt(i));
        }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataLength, true);
    
    let offset = 44;
    for (let i = 0; i < buffer.length; i++) {
        for (let ch = 0; ch < numChannels; ch++) {
            const sample = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
            view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
            offset += 2;
        }
    }
    
    return arrayBuffer;
}